import { Loader2, AlertTriangle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import BoutonMotDePasse from '@/shared/components/BoutonMotDePasse';

/**
 * Création et modification d'un compte poseur, puis la confirmation des gestes qui
 * coupent un accès.
 *
 * Deux points méritent d'être dits.
 *
 * Le mot de passe est saisi par l'installateur et transmis de la main à la main : il n'y
 * a pas d'envoi d'e-mail dans ce parcours. L'écran le rappelle explicitement, faute de
 * quoi un installateur créerait un compte en supposant que son poseur recevra quelque
 * chose — et le poseur attendrait un message qui ne viendra jamais.
 *
 * Les confirmations passent par un dialogue de l'application et non par `window.confirm`.
 * Ce n'est pas un écart de fond avec sa version — le geste, le texte et la portée sont
 * les siens — mais une alerte native ne se teste pas, ne se traduit pas, et ne dit pas
 * en rouge qu'elle est irréversible.
 */

export function PoseurDialog({ etat, enregistrement, t, onChange, onFermer, onEnregistrer }) {
  const creation = etat?.mode === 'create';
  const d = etat?.data;
  const incomplet = !d?.prenom || !d?.nom || !d?.email || (creation && !d?.password);

  return (
    <Dialog open={!!etat} onOpenChange={(o) => { if (!o) onFermer(); }}>
      <DialogContent className="sm:max-w-md" data-testid="poseur-dialog">
        <DialogHeader>
          <DialogTitle>
            {creation ? t('poseur_dialog_create_title') : t('poseur_dialog_edit_title')}
          </DialogTitle>
          <DialogDescription>
            {creation ? t('poseur_dialog_create_desc') : t('poseur_dialog_edit_desc')}
          </DialogDescription>
        </DialogHeader>

        {etat && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-neutral-500">{t('field_prenom')}</Label>
                <Input
                  className="h-11 text-[14px]"
                  value={d.prenom}
                  onChange={(e) => onChange({ prenom: e.target.value })}
                  data-testid="poseur-prenom"
                />
              </div>
              <div>
                <Label className="text-[11px] text-neutral-500">{t('field_nom')}</Label>
                <Input
                  className="h-11 text-[14px]"
                  value={d.nom}
                  onChange={(e) => onChange({ nom: e.target.value })}
                  data-testid="poseur-nom"
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px] text-neutral-500">{t('field_email')}</Label>
              <Input
                type="email"
                className="h-11 text-[14px]"
                value={d.email}
                onChange={(e) => onChange({ email: e.target.value })}
                data-testid="poseur-email"
              />
            </div>
            <div>
              <Label className="text-[11px] text-neutral-500">
                {creation
                  ? t('field_password_temp')
                  : `${t('field_password')} ${t('field_password_optional')}`}
              </Label>
              {/* En clair une fois généré : le poseur le reçoit de vive voix, et un
                  champ masqué obligerait à le retaper pour le lui dicter. */}
              <Input
                type={d.password ? 'text' : 'password'}
                className="h-11 text-[14px]"
                placeholder={creation ? t('password_hint_min') : t('password_hint_new')}
                value={d.password}
                onChange={(e) => onChange({ password: e.target.value })}
                data-testid="poseur-password"
              />
              <div className="mt-1.5">
                <BoutonMotDePasse valeur={d.password} tid="poseur-mdp"
                                  onGenere={(m) => onChange({ password: m })} />
              </div>
              <p className="text-[10.5px] text-neutral-400 mt-1">{t('password_share_hint')}</p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onFermer}>{t('btn_cancel')}</Button>
          <Button
            onClick={onEnregistrer}
            disabled={enregistrement || incomplet}
            data-testid="poseur-save-btn"
          >
            {enregistrement
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('saving')}</>
              : (creation ? t('btn_create_account') : t('btn_save'))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * La confirmation avant de désactiver, réactiver ou supprimer un poseur.
 *
 * `etat` porte { poseur, geste } où geste vaut 'basculer' ou 'supprimer'. La suppression
 * est la seule des trois à être annoncée comme irréversible, et c'est la seule à l'être.
 */
export function ConfirmationPoseur({ etat, t, onFermer, onConfirmer }) {
  if (!etat) return null;
  const { poseur, geste } = etat;
  const suppression = geste === 'supprimer';
  const libelle = suppression
    ? t('team_delete')
    : (poseur.active ? t('confirm_deactivate') : t('confirm_reactivate'));

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onFermer(); }}>
      <DialogContent className="sm:max-w-md" data-testid="poseur-confirm-dialog">
        <DialogHeader>
          <DialogTitle className={suppression ? 'text-red-700' : undefined}>
            {suppression ? t('confirm_delete_poseur_title') : `${libelle} ${poseur.name} ?`}
          </DialogTitle>
          {suppression && (
            <DialogDescription className="flex items-start gap-2 text-red-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span data-testid="poseur-confirm-body">{t('confirm_delete_poseur_body')}</span>
            </DialogDescription>
          )}
        </DialogHeader>
        <p className="text-[13px] font-medium text-neutral-800">{poseur.name}</p>
        <DialogFooter>
          <Button variant="outline" onClick={onFermer} data-testid="poseur-confirm-cancel">
            {t('btn_cancel')}
          </Button>
          <Button
            variant={suppression ? 'destructive' : 'default'}
            onClick={onConfirmer}
            data-testid="poseur-confirm-ok"
          >
            {libelle}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
